import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ArrowRight, Check, X, Lightbulb, RotateCcw, ChevronRight, Dumbbell } from "lucide-react";
import { getPractice, practiceQuiz } from "@/lib/clientApi";
import { TOPIC_MAP } from "@/lib/topics";

export default function Practice() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const [phase, setPhase] = useState("pick"); // pick | loading | play | done
  const [topic, setTopic] = useState(null);
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);

  useEffect(() => {
    (async () => {
      try { setInfo(await getPractice(token)); }
      catch (e) { setError(true); }
    })();
  }, [token]);

  const startTopic = async (t) => {
    setTopic(t);
    setPhase("loading");
    try {
      const data = await practiceQuiz(token, t.id);
      setItems(data.questions);
      setIdx(0); setSelected(null); setLocked(false); setCorrectCount(0);
      setPhase("play");
    } catch (e) {
      setError(true);
    }
  };

  const answer = (i) => {
    if (locked) return;
    setSelected(i); setLocked(true);
    if (i === items[idx].correctIndex) setCorrectCount((c) => c + 1);
  };

  const next = () => {
    if (idx + 1 >= items.length) { setPhase("done"); return; }
    setIdx((n) => n + 1); setSelected(null); setLocked(false);
  };

  if (error) return <Centered><p className="font-display text-2xl font-bold text-slate-700 mb-2">This link isn't valid</p><p className="text-slate-500 font-semibold">Please check with your adviser for a fresh link.</p></Centered>;
  if (!info) return <Centered><Loader2 className="w-10 h-10 text-[#29ABE2] animate-spin" /></Centered>;

  const accent = topic ? TOPIC_MAP[topic.id] : null;
  const color = accent?.color || "#29ABE2";
  const deep = accent?.deep || "#005580";

  return (
    <div className="min-h-screen bg-[#F8FAFC] relative overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-sky-200/40 blur-3xl floaty" />
      <div className="relative max-w-xl mx-auto px-5 pt-8 flex justify-center">
        <img src="/eyb-logo-full.png" alt="EYB Consulting" className="h-9 w-auto" />
      </div>

      <div className="relative max-w-xl mx-auto px-5 pt-8 pb-24">
        <AnimatePresence mode="wait">
          {phase === "pick" && (
            <motion.div key="pick" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="text-center mb-8">
                <div className="text-6xl mb-3">🏋️</div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#005580] mb-2">
                  Practice zone{info.business_name ? ` · ${info.business_name}` : ""}
                </h1>
                <p className="text-slate-500 font-semibold text-lg">
                  Pick a topic and practise at your own pace. You'll see the answer and a quick explanation after each question. No scores, no pressure.
                </p>
              </div>
              <div className="space-y-3">
                {info.topics.map((t) => {
                  const a = TOPIC_MAP[t.id];
                  return (
                    <button
                      key={t.id}
                      data-testid={`practice-topic-${t.id}`}
                      onClick={() => startTopic(t)}
                      className="w-full flex items-center gap-4 rounded-2xl border-2 border-b-4 border-slate-200 bg-white px-5 py-4 text-left hover:border-slate-300 active:border-b-2 active:translate-y-[2px] transition-all"
                    >
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: a?.tint || "#E0F2FE" }}>
                        <ChevronRight className="w-5 h-5" style={{ color: a?.deep || "#005580" }} strokeWidth={3} />
                      </div>
                      <span className="font-display font-bold text-lg text-slate-800">{t.name}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {phase === "loading" && (
            <motion.div key="loading" className="text-center pt-20">
              <Loader2 className="w-12 h-12 mx-auto animate-spin" style={{ color }} />
              <p className="font-display text-2xl font-bold text-[#005580] mt-6">Building your practice set…</p>
              <p className="text-slate-400 font-semibold">Fresh questions tailored to your business.</p>
            </motion.div>
          )}

          {phase === "play" && items[idx] && (
            <motion.div key={`q-${idx}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div className="h-full rounded-full" style={{ background: color }} animate={{ width: `${((idx + (locked ? 1 : 0)) / items.length) * 100}%` }} />
                </div>
                <span className="text-xs font-bold text-slate-400">{idx + 1} / {items.length}</span>
              </div>

              <div className="text-5xl text-center mb-4">{items[idx].emoji}</div>
              <h2 className="font-display font-bold text-xl sm:text-2xl text-slate-800 text-center mb-6 leading-snug" data-testid="practice-question">
                {items[idx].question}
              </h2>

              <div className="space-y-3">
                {items[idx].options.map((opt, i) => {
                  const isCorrect = i === items[idx].correctIndex;
                  const isSel = i === selected;
                  let cls = "bg-white border-slate-200 text-slate-700";
                  if (locked) {
                    if (isCorrect) cls = "bg-emerald-50 border-emerald-400 text-emerald-800";
                    else if (isSel) cls = "bg-rose-50 border-rose-400 text-rose-800";
                    else cls = "bg-white border-slate-100 text-slate-400";
                  }
                  return (
                    <button key={i} data-testid={`practice-option-${i}`} disabled={locked} onClick={() => answer(i)}
                      className={`w-full text-left rounded-2xl border-2 border-b-4 px-5 py-4 font-bold transition-all flex items-center justify-between gap-3 ${cls} ${!locked ? "hover:border-slate-300" : ""}`}>
                      <span className="text-[15px] leading-snug">{opt}</span>
                      {locked && isCorrect && <Check className="w-5 h-5 text-emerald-500 shrink-0" strokeWidth={3} />}
                      {locked && isSel && !isCorrect && <X className="w-5 h-5 text-rose-500 shrink-0" strokeWidth={3} />}
                    </button>
                  );
                })}
              </div>

              {locked && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  className={`mt-5 rounded-2xl border-2 p-4 ${selected === items[idx].correctIndex ? "bg-emerald-50 border-emerald-200" : "bg-sky-50 border-sky-200"}`}>
                  <div className="flex items-start gap-2 mb-3">
                    <Lightbulb className="w-5 h-5 mt-0.5 shrink-0 text-amber-500" />
                    <p className="text-slate-700 text-sm font-semibold leading-snug" data-testid="practice-explanation">{items[idx].explanation}</p>
                  </div>
                  <button data-testid="practice-continue-btn" onClick={next}
                    className="w-full text-white font-bold rounded-2xl py-3 border-b-4 active:border-b-0 active:translate-y-[3px] transition-all flex items-center justify-center gap-2"
                    style={{ background: color, borderColor: deep }}>
                    {idx + 1 >= items.length ? "Finish" : "Next question"} <ArrowRight className="w-5 h-5" strokeWidth={3} />
                  </button>
                </motion.div>
              )}
            </motion.div>
          )}

          {phase === "done" && (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center pt-8">
              <div className="text-6xl mb-4">🎉</div>
              <h1 className="font-display text-4xl font-bold text-[#005580] mb-2">Nice practising!</h1>
              <p className="text-slate-500 font-semibold text-lg mb-8">
                You got {correctCount} out of {items.length} this round. Every rep builds your money confidence.
              </p>
              <div className="space-y-3">
                <button data-testid="practice-again-btn" onClick={() => startTopic(topic)}
                  className="w-full flex items-center justify-center gap-2 text-white font-bold rounded-2xl py-4 border-b-4 active:border-b-0 active:translate-y-[3px] transition-all"
                  style={{ background: color, borderColor: deep }}>
                  <RotateCcw className="w-5 h-5" strokeWidth={2.5} /> Practise this topic again
                </button>
                <button data-testid="practice-pick-another-btn" onClick={() => { setPhase("pick"); setTopic(null); }}
                  className="w-full font-bold rounded-2xl py-3.5 border-2 border-b-4 border-slate-200 bg-white text-slate-700 active:border-b-2 active:translate-y-[2px] transition-all">
                  Pick another topic
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

const Centered = ({ children }) => (
  <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center text-center px-6">{children}</div>
);
